/* eslint-disable */
// VENDORED VERBATIM from the MMEC TradingView overlay (Pepsi repo, src/processor.js @ 594c8e5).
// This is the level engine the desktop tool runs; do not edit here — re-copy from the overlay repo.
/**
 * src/processor.js
 * Corrected gamma calculation methodology — TanukiTrade spec, July 2026.
 *
 * Key corrections:
 *   1. GF = max |Spot Gamma|  (PEAK, not zero-cross)
 *   2. ZG = cumulative GEX zero-cross (separate from GF)
 *   3. Sign = -1 for BOTH calls and puts (dealer/seller perspective)
 *   4. DTE weighting: 1/(DTE + 0.1)
 *   5. Volume freshness: OI × (1 + min(vol/OI, 1.0))
 *   6. Call Wall = max CALL spot gamma only
 *   7. Put Wall  = max |PUT spot gamma| only
 *   8. GF Zone with confidence rating
 *   9. GF-ZG Divergence signal
 */

import { yearsToExpiry } from "./strikeIv.js";

const CONTRACT_SIZE = 100;

// Unknown-DTE bucket key (rows with unparseable expiry)
export const UNKNOWN_DTE = "unknown";

function safeNum(v) {
  if (v == null || v === "" || v === "-") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function isCall(row) {
  const t = String(row["Option Type"] ?? row.optionType ?? "").toUpperCase();
  return t === "C" || t === "CALL";
}

/** Parse common expiry formats; returns Date at UTC midnight or null. */
function parseExpiry(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const d = new Date(ms);
    return isNaN(d) ? null : d;
  }
  const s = String(raw).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) {
    let y = +m[3];
    if (y < 100) y += 2000;
    return new Date(Date.UTC(y, +m[1] - 1, +m[2]));
  }
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

/** Days between today's UTC midnight and an expiry Date. */
function dteFor(row, todayUtc) {
  const raw =
    row["Expiration Date"] ??
    row["Expiration"] ??
    row.expiry ??
    row.expirationDate ??
    row.expiration ??
    row["Expiry Date"];
  const d = parseExpiry(raw);
  if (!d) return UNKNOWN_DTE;
  const days = Math.round((d.getTime() - todayUtc) / 86400000);
  return Math.max(days, 0);
}

/**
 * Row IV as a decimal, or 0 when the chain carries none. The stable feed puts
 * the value in "Implied Vol"; "Implied Volatility" is 0 there.
 */
function rowIvDec(r) {
  for (const k of ["Implied Volatility", "Implied Vol", "implied_vol", "implied_volatility", "iv"]) {
    const v = safeNum(r[k]);
    if (v > 0) return v > 2 ? v / 100 : v;
  }
  return 0;
}

function getGamma(row) {
  // The feed's gamma or nothing. The old fallback invented one from a fixed
  // 20% IV and a 30-day expiry and fed it into the GEX levels.
  const g = safeNum(row["Gamma"] ?? row.Gamma ?? row.gamma);
  return g > 1e-12 ? g : 0;
}

function getUnderlyingPrice(rows) {
  for (const row of rows.slice(0, 10)) {
    const v = safeNum(
      row["Underlying Price"] ??
        row.UnderlyingPrice ??
        row.underlying_price ??
        row.spot,
    );
    if (v > 0) return v;
  }
  return 0;
}

/**
 * CORRECTED: Aggregate rows into one level group.
 * Follows TanukiTrade methodology spec (July 2026).
 */
function aggregate(rows, spot) {
  if (!rows.length) return null;

  const S = spot || getUnderlyingPrice(rows);
  if (!(S > 0)) return null; // no real spot → no levels (was: pretend spot = 100)
  const todayUtc = (() => {
    const d = new Date();
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  })();

  // Per-strike accumulators
  const byStrike = new Map();

  for (const row of rows) {
    const strike = safeNum(row["Strike"] ?? row.strike ?? row["Strike Price"]);
    if (strike <= 0 || S <= 0) continue;

    const gamma = getGamma(row, S);
    const oi = safeNum(
      row["Open Interest"] ?? row.OpenInterest ?? row.open_interest,
    );
    const vol = safeNum(row["Volume"] ?? row.volume ?? row.Vol ?? row.vol);
    const call = isCall(row);

    // ── DTE weighting (disabled — each expiry should contribute proportionally) ──
    const dteWeight = 1.0;

    // ── Volume freshness ──
    const effectiveOI = oi * (1 + Math.min(vol / Math.max(oi, 1), 1.0));

    // ── Spot Gamma: DEALER perspective (sign = -1 for both C and P) ──
    const spotGamma = -1 * gamma * effectiveOI * (S * S) * 0.01 * dteWeight;

    // ── GEX: traditional sign (+1 calls, -1 puts) so cumulative can cross zero ──
    const gexSign = call ? 1 : -1;
    const gex = gexSign * gamma * effectiveOI * CONTRACT_SIZE * S * 0.01 * dteWeight;

    // ── Volume-weighted gamma (parallel to OI above, but using raw Volume) ──
    // Volume GF / Volume Max / Volume Min are derived from these. Prefer the
    // accumulated (all-day) volume merged in from source=rtl_legacy, since the
    // stable snapshot's `Volume` is zero. Fall back to intraday Volume if the
    // accumulated value was not attached.
    const effectiveVol =
      safeNum(
        row["Accumulated Volume"] ?? row.accumulatedVolume ?? row.accVolume,
      ) || vol;
    const volSpotGamma =
      -1 * gamma * effectiveVol * (S * S) * 0.01 * dteWeight;
    const volGex =
      gexSign * gamma * effectiveVol * CONTRACT_SIZE * S * 0.01 * dteWeight;

    if (!byStrike.has(strike)) {
      byStrike.set(strike, {
        strike,
        callOI: 0,
        putOI: 0,
        callVol: 0,
        putVol: 0,
        netSpotGamma: 0,
        callSpotGamma: 0, // calls only — for Call Wall
        putSpotGamma: 0, // puts only  — for Put Wall
        netGex: 0,
        // Volume-weighted gamma (for Volume GF / Max / Min)
        netVolSpotGamma: 0,
        netVolGex: 0,
      });
    }
    const agg = byStrike.get(strike);
    agg.netSpotGamma += spotGamma;
    agg.netGex += gex;
    agg.netVolSpotGamma += volSpotGamma;
    agg.netVolGex += volGex;
    if (call) {
      agg.callOI += effectiveOI;
      agg.callVol += vol;
      agg.callSpotGamma += spotGamma;
    } else {
      agg.putOI += effectiveOI;
      agg.putVol += vol;
      agg.putSpotGamma += spotGamma;
    }
  }

  const strikes = [...byStrike.values()].sort((a, b) => a.strike - b.strike);
  if (!strikes.length) return null;

  // ── Zero Gamma = cumulative GEX zero-cross ──
  let cumGex = 0,
    zeroGammaStrike = null;
  for (let i = 0; i < strikes.length; i++) {
    const prev = cumGex;
    cumGex += strikes[i].netGex;
    if (
      zeroGammaStrike === null &&
      ((prev < 0 && cumGex >= 0) || (prev > 0 && cumGex <= 0))
    ) {
      zeroGammaStrike = strikes[i].strike;
    }
  }

  // ── Gamma Flip = max |netSpotGamma| (PEAK) ──
  const gammaFlipEntry = strikes.reduce(
    (m, s) => (Math.abs(s.netSpotGamma) > Math.abs(m.netSpotGamma) ? s : m),
    strikes[0],
  );
  const gammaFlip = gammaFlipEntry.strike;

  // ── GF Zone (80% of max) ──
  const maxGamma = Math.abs(gammaFlipEntry.netSpotGamma);
  const gfZoneStrikes = strikes
    .filter((s) => Math.abs(s.netSpotGamma) >= 0.8 * maxGamma)
    .map((s) => s.strike);
  const gfZoneMin = gfZoneStrikes.length
    ? Math.min(...gfZoneStrikes)
    : gammaFlip;
  const gfZoneMax = gfZoneStrikes.length
    ? Math.max(...gfZoneStrikes)
    : gammaFlip;
  const gfWidth = (gfZoneMax - gfZoneMin) / S;
  const gfConfidence =
    gfWidth < 0.003 ? "HIGH" : gfWidth < 0.01 ? "MEDIUM" : "LOW";

  // ── Call Wall = strike with max CALL open interest (traditional definition) ──
  // Using raw OI (not gamma) so the wall reflects real dealer positioning at
  // strikes away from spot — gamma always peaks ATM and would cluster every
  // level on top of Gamma Flip if used here too.
  const callWall = strikes.reduce(
    (m, s) => (s.callOI > m.callOI ? s : m),
    strikes[0],
  );

  // ── Put Wall = strike with max PUT open interest ──
  const putWall = strikes.reduce(
    (m, s) => (s.putOI > m.putOI ? s : m),
    strikes[0],
  );

  // ── Volume-weighted levels (Volume GF / Volume Max / Volume Min) ──
  // These mirror the OI gamma profile but use raw Volume instead of OI, so
  // they reflect intraday flow rather than static positioning.
  //
  //   Volume GF  = peak |netVolSpotGamma|  (most-concentrated volume gamma)
  //   Volume Max = strike with max netVolGex  (peak of per-strike vol GEX)
  //   Volume Min = strike with min netVolGex  (dip of per-strike vol GEX)
  const volGammaFlipEntry = strikes.reduce(
    (m, s) =>
      Math.abs(s.netVolSpotGamma) > Math.abs(m.netVolSpotGamma) ? s : m,
    strikes[0],
  );
  const volGammaFlip = volGammaFlipEntry.strike;
  const volMaxEntry = strikes.reduce(
    (m, s) => (s.netVolGex > m.netVolGex ? s : m),
    strikes[0],
  );
  const volMinEntry = strikes.reduce(
    (m, s) => (s.netVolGex < m.netVolGex ? s : m),
    strikes[0],
  );
  const volMaxGex = volMaxEntry.strike;
  const volMinGex = volMinEntry.strike;

  // ── GF-ZG Divergence ──
  let divergence = 0,
    divSignal = "NEUTRAL";
  if (zeroGammaStrike) {
    divergence = Math.abs(gammaFlip - zeroGammaStrike) / S;
    divSignal =
      divergence < 0.003
        ? "NEUTRAL"
        : zeroGammaStrike > gammaFlip
          ? "BULLISH"
          : "BEARISH";
  }

  // ── Wall Strength: % of total call/put OI concentrated at the wall ──
  const totalCallOI = strikes.reduce((sum, s) => sum + s.callOI, 0);
  const totalPutOI = strikes.reduce((sum, s) => sum + s.putOI, 0);
  const callWallStr = totalCallOI ? callWall.callOI / totalCallOI : 0;
  const putWallStr = totalPutOI ? putWall.putOI / totalPutOI : 0;

  // ── Net GEX ──
  const netGex = cumGex;

  // ── Extended metrics (IVR, skew, exp move) ──
  const extended = computeExtendedMetrics(
    rows,
    S,
    strikes,
    callWall,
    putWall,
    gammaFlipEntry,
  );

  return {
    spot: S,
    strikes,
    // Primary levels
    gammaFlip,
    zeroGamma: zeroGammaStrike,
    // GF Zone
    gfZoneMin,
    gfZoneMax,
    gfWidth: +(gfWidth * 100).toFixed(2), // percentage
    gfConfidence,
    // Walls (corrected: calls/puts only)
    callWall: callWall.strike,
    putWall: putWall.strike,
    callWallOI: callWall.callOI,
    putWallOI: putWall.putOI,
    callWallStrength: +callWallStr.toFixed(3),
    putWallStrength: +putWallStr.toFixed(3),
    // Volume-weighted levels
    volGammaFlip,
    volMaxGex,
    volMinGex,
    // Divergence
    gfZgDivergence: +(divergence * 100).toFixed(2),
    divSignal,
    // Net GEX
    netGex,
    // Per-strike gamma for detailed view
    netSpotGamma: gammaFlipEntry.netSpotGamma,
    // Legacy compat
    top5ByOI: [...strikes]
      .sort((a, b) => b.callOI + b.putOI - (a.callOI + a.putOI))
      .slice(0, 5),
    top5ByVol: [...strikes]
      .sort((a, b) => b.callVol + b.putVol - (a.callVol + a.putVol))
      .slice(0, 5),
    // Extended
    ivRank: extended.ivRank,
    ivAvg: extended.ivAvg,
    skewPct: extended.skewPct,
    callIV: extended.callIV,
    putIV: extended.putIV,
    expMove: extended.expMove,
    expUpper: extended.expUpper,
    expLower: extended.expLower,
    minDte: extended.minDte,
  };
}

/** Compute extended metrics: IVR, skew, expected move from raw rows */
function computeExtendedMetrics(
  rows,
  spot,
  strikes,
  callWall,
  putWall,
  gammaFlipEntry,
) {
  // Everything below comes from the chain's own values; anything the chain
  // doesn't carry is null — never a stand-in (was: IV rank 50, IV 20%, 30-day
  // expiry, skew 0). `strikes` never carried IV, so the old average was
  // always 0 and every figure here came from those defaults.
  const expOf = (r) => {
    const d = parseExpiry(r["Expiry Date"] ?? r["Expiration Date"] ?? r["Expiration"] ?? r.expiry ?? r.expiry_date);
    return d ? d.toISOString().slice(0, 10).replace(/-/g, "") : "";
  };
  const todayYmd = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  let nearest = "";
  for (const r of rows) {
    const e = expOf(r);
    if (e && e >= todayYmd && (!nearest || e < nearest)) nearest = e;
  }
  // ATM IV: nearest expiry, strikes within ±1% of spot. Range: ±10% of spot.
  let tIV = 0,
    nIV = 0,
    lo = Infinity,
    hi = -Infinity;
  for (const r of rows) {
    const K = safeNum(r.Strike ?? r.strike ?? 0), iv = rowIvDec(r);
    if (!K || iv <= 0 || iv > 5) continue;
    if (Math.abs(K - spot) <= spot * 0.1) { if (iv < lo) lo = iv; if (iv > hi) hi = iv; }
    if (expOf(r) === nearest && Math.abs(K - spot) <= spot * 0.01) { tIV += iv; nIV++; }
  }
  const ivAvg = nIV ? tIV / nIV : null;
  const ivRank = ivAvg != null && hi > lo ? Math.round(((ivAvg - lo) / (hi - lo)) * 100) : null;

  // Skew
  let ci = 0,
    pi = 0,
    cn = 0,
    pn = 0;
  const d5 = spot * 0.05;
  for (const r of rows) {
    const K = safeNum(r.Strike ?? r.strike ?? 0);
    if (!K) continue;
    const iv = rowIvDec(r);
    if (iv <= 0) continue;
    const isC = String(r["Option Type"] ?? "").toUpperCase() === "C";
    if (isC && K >= spot + d5 * 0.7 && K <= spot + d5 * 1.3) {
      ci += iv;
      cn++;
    }
    if (!isC && K <= spot - d5 * 0.7 && K >= spot - d5 * 1.3) {
      pi += iv;
      pn++;
    }
  }
  const ca = cn ? ci / cn : null,
    pa = pn ? pi / pn : null;
  const skewPct =
    ca && pa ? +(((pa - ca) / ((ca + pa) / 2)) * 100).toFixed(1) : null;

  // Expected move to the nearest expiry's close (16:00 New York), from ATM IV.
  const T = nearest ? yearsToExpiry(nearest) : null;
  const move = ivAvg != null && T ? spot * ivAvg * Math.sqrt(T) : null;
  const ymdUtc = (s) => Date.UTC(+s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8));
  const minDte = nearest ? Math.round((ymdUtc(nearest) - ymdUtc(todayYmd)) / 864e5) : null;

  return {
    ivRank,
    ivAvg: ivAvg == null ? null : +(ivAvg * 100).toFixed(1),
    skewPct,
    callIV: ca == null ? null : +(ca * 100).toFixed(1),
    putIV: pa == null ? null : +(pa * 100).toFixed(1),
    expMove: move == null ? null : +move.toFixed(0),
    expUpper: move == null ? null : +(spot + move).toFixed(0),
    expLower: move == null ? null : +(spot - move).toFixed(0),
    minDte,
  };
}

/** Back-compat: aggregate ALL rows into one level group. */
export function processRows(rows) {
  if (!rows || !rows.length) return null;
  const spot = getUnderlyingPrice(rows);
  if (spot <= 0)
    console.warn("[processor] Could not determine underlying price from data");
  return aggregate(rows, spot);
}

/**
 * Group rows by DTE, aggregate each group separately.
 */
export function processRowsByDTE(rows, dteFilter = ["all"]) {
  if (!rows || !rows.length) return [];
  const spot = getUnderlyingPrice(rows);
  const todayUtc = (() => {
    const d = new Date();
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  })();

  const byDte = new Map();
  for (const row of rows) {
    const dte = dteFor(row, todayUtc);
    if (!byDte.has(dte)) byDte.set(dte, []);
    byDte.get(dte).push(row);
  }

  const out = [];
  for (const wanted of dteFilter) {
    if (wanted === "all") {
      const lv = aggregate(rows, spot);
      if (lv) out.push({ dte: "all", count: rows.length, levels: lv });
      continue;
    }
    const subset = byDte.get(Number(wanted)) || [];
    if (!subset.length) {
      console.warn(`[processor] No rows for DTE=${wanted}`);
      continue;
    }
    const lv = aggregate(subset, spot);
    if (lv) out.push({ dte: Number(wanted), count: subset.length, levels: lv });
  }
  return out;
}

/**
 * Discover which DTE values actually exist in the fetched rows.
 */
export function discoverDtes(rows) {
  if (!rows || !rows.length) return [];
  const todayUtc = (() => {
    const d = new Date();
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  })();
  const dtes = new Set();
  for (const row of rows) {
    const dte = dteFor(row, todayUtc);
    if (dte !== UNKNOWN_DTE) dtes.add(dte);
  }
  return [...dtes].sort((a, b) => a - b);
}

// ════════════════════════════════════════════════════════════
// Extended Metrics — IV Rank, Skew, Expected Move, Delta Curves
// ════════════════════════════════════════════════════════════

export function computeIVRank(rows) {
  let total = 0,
    n = 0,
    lo = Infinity,
    hi = -Infinity;
  for (const r of rows) {
    const iv = rowIvDec(r);
    if (iv <= 0 || iv > 5) continue;
    total += iv;
    n++;
    if (iv < lo) lo = iv;
    if (iv > hi) hi = iv;
  }
  if (!n) return null;
  const avg = total / n;
  const rank = hi > lo ? ((avg - lo) / (hi - lo)) * 100 : null; // no spread → no rank (was 50)
  return {
    avgIV: +(avg * 100).toFixed(1),
    ivRank: rank == null ? null : Math.round(rank),
    lo: +(lo * 100).toFixed(1),
    hi: +(hi * 100).toFixed(1),
  };
}

export function computeSkew(rows, spot) {
  if (!spot) return null;
  const d = spot * 0.05;
  let cIV = 0,
    pIV = 0,
    cn = 0,
    pn = 0;
  for (const r of rows) {
    const K = safeNum(r.Strike ?? r.strike ?? 0);
    if (!K) continue;
    const iv = rowIvDec(r);
    if (iv <= 0) continue;
    const isC = String(r["Option Type"] ?? "").toUpperCase() === "C";
    if (isC && K >= spot + d * 0.7 && K <= spot + d * 1.3) {
      cIV += iv;
      cn++;
    }
    if (!isC && K <= spot - d * 0.7 && K >= spot - d * 1.3) {
      pIV += iv;
      pn++;
    }
  }
  if (!cn || !pn) return null;
  const ca = cIV / cn,
    pa = pIV / pn;
  const skew = ((pa - ca) / ((ca + pa) / 2)) * 100;
  return {
    callIV: +(ca * 100).toFixed(1),
    putIV: +(pa * 100).toFixed(1),
    skewPct: +skew.toFixed(1),
    dir: skew > 2 ? "PUT↑" : skew < -2 ? "CALL↑" : "NEUTRAL",
  };
}

export function computeExpectedMove(rows, spot) {
  if (!spot) return null;
  let iv = 0,
    n = 0;
  for (const r of rows) {
    const K = safeNum(r.Strike ?? r.strike ?? 0);
    if (!K || Math.abs(K - spot) / spot > 0.02) continue;
    const v = rowIvDec(r);
    if (v <= 0) continue;
    iv += v;
    n++;
  }
  if (!n) return null;
  iv /= n;
  let dte = 365;
  const today = Date.UTC(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth(),
    new Date().getUTCDate(),
  );
  for (const r of rows) {
    const d = parseExpiry(r["Expiration Date"] ?? r.expiry);
    if (!d) continue;
    const days = Math.round((d.getTime() - today) / 864e5);
    if (days > 0 && days < dte) dte = days;
  }
  const move = spot * iv * Math.sqrt(dte / 365.25);
  return {
    upper: +(spot + move).toFixed(0),
    lower: +(spot - move).toFixed(0),
    move: +move.toFixed(0),
    iv: +(iv * 100).toFixed(1),
    dte,
  };
}

export function computeHorizontalSkew(rows, spot) {
  if (!spot) return [];
  const today = Date.UTC(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth(),
    new Date().getUTCDate(),
  );
  const byExp = {};
  for (const r of rows) {
    const d = parseExpiry(r["Expiration Date"] ?? r.expiry);
    if (!d) continue;
    const dte = Math.round((d.getTime() - today) / 864e5);
    if (dte <= 0) continue;
    const K = safeNum(r.Strike ?? r.strike ?? 0);
    if (Math.abs(K - spot) / spot > 0.03) continue;
    const v = rowIvDec(r);
    if (v <= 0) continue;
    if (!byExp[dte])
      byExp[dte] = { t: 0, n: 0, date: d.toISOString().slice(0, 10) };
    byExp[dte].t += v;
    byExp[dte].n++;
  }
  const exps = Object.entries(byExp)
    .map(([dte, v]) => ({
      dte: +dte,
      date: v.date,
      ivx: +((v.t / v.n) * 100).toFixed(1),
    }))
    .sort((a, b) => a.dte - b.dte);
  const out = [];
  for (let i = 1; i < exps.length; i++) {
    const diff = +(exps[i].ivx - exps[i - 1].ivx).toFixed(1);
    out.push({ from: exps[i - 1], to: exps[i], diff, inverted: diff < -0.5 });
  }
  return out;
}

export function computeDeltaCurves(rows, spot) {
  if (!spot) return null;
  let iv = 0,
    n = 0;
  for (const r of rows) {
    const K = safeNum(r.Strike ?? r.strike ?? 0);
    if (!K || Math.abs(K - spot) / spot > 0.02) continue;
    const v = rowIvDec(r);
    if (v <= 0) continue;
    iv += v;
    n++;
  }
  if (!n) return null;
  iv /= n;
  let dte = 30;
  const today = Date.UTC(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth(),
    new Date().getUTCDate(),
  );
  for (const r of rows) {
    const d = parseExpiry(r["Expiration Date"] ?? r.expiry);
    if (!d) continue;
    const days = Math.round((d.getTime() - today) / 864e5);
    if (days > 0 && days < dte) dte = days;
  }
  const T = dte / 365.25,
    sqrtT = Math.sqrt(T);
  function findStrike(targetDelta) {
    let g = spot * 1.1,
      prev = g;
    for (let i = 0; i < 30; i++) {
      const d1 =
        (Math.log(g / spot) + (0.05 + (iv * iv) / 2) * T) / (iv * sqrtT);
      const nd1 = Math.exp(-0.5 * d1 * d1) / Math.sqrt(2 * Math.PI);
      const cd = (() => {
        const a1 = 0.254829592,
          a2 = -0.284496736,
          a3 = 1.421413741,
          a4 = -1.453152027,
          a5 = 1.061405429,
          p = 0.3275911,
          s = d1 < 0 ? -1 : 1,
          x = Math.abs(d1) / Math.SQRT2,
          t = 1 / (1 + p * x);
        return (
          0.5 *
          (1 +
            s *
              (1 -
                ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) *
                  t *
                  Math.exp(-x * x)))
        );
      })();
      const err = 1 - cd - targetDelta;
      if (Math.abs(err) < 0.0001) break;
      const vega = nd1 / (spot * iv * sqrtT) || 0.001;
      g = g - err / vega;
      if (g <= spot * 0.5 || g >= spot * 2) {
        g = prev * 1.05;
        break;
      }
    }
    return g;
  }
  return {
    calls: {
      16: +findStrike(0.16).toFixed(0),
      20: +findStrike(0.2).toFixed(0),
      25: +findStrike(0.25).toFixed(0),
      30: +findStrike(0.3).toFixed(0),
    },
    puts: {
      16: +(spot - (findStrike(0.16) - spot)).toFixed(0),
      20: +(spot - (findStrike(0.2) - spot)).toFixed(0),
      25: +(spot - (findStrike(0.25) - spot)).toFixed(0),
      30: +(spot - (findStrike(0.3) - spot)).toFixed(0),
    },
  };
}
